import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PrivateAxios } from '../../request/axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Délai (en secondes) avant de pouvoir redemander un code (recommandation UX : 60 s, login inclus).
const RESEND_COOLDOWN = 60;

const Login = () => {

    // 'credentials' = saisie identifiant/mot de passe, 'code' = saisie du code 2FA reçu par email.
    const [step, setStep] = useState('credentials');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [isOnline, setIsOnline] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (location.state?.message) {
            toast.info(location.state.message);
        }

        const message = localStorage.getItem('logoutMessage');
        if (message) {
            // Afficher le message avec react-toastify
            toast.info(message);
            // Supprimer le message du localStorage pour éviter de le réafficher
            localStorage.removeItem('logoutMessage');
        }

        const checkIsAlive = () => {
            setIsOnline(true);
        };

        checkIsAlive();
    }, [location]);

    // Compte à rebours du bouton « Renvoyer le code ».
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    // Extrait les infos utiles d'une erreur axios (statut, message serveur, Retry-After).
    const extractError = (error) => ({
        status: error.response?.status,
        message: error.response?.data?.message || error.message,
        retryAfter: parseInt(error.response?.headers?.['retry-after'], 10),
    });

    // Revenir à l'écran identifiants (session 2FA expirée / garde-fou renvois).
    const backToCredentials = () => {
        setStep('credentials');
        setCode('');
        setPassword('');
        setResendCooldown(0);
    };

    // Étape 1 : identifiant + mot de passe → déclenche l'envoi du code par email.
    const handleLogin = async (event) => {
        event.preventDefault();
        if (!isOnline) {
            console.log('Application down, cannot connect.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await PrivateAxios.post('/login', { username, password });

            if (response.status === 200 && response.data['2fa_required']) {
                setStep('code');
                setCode('');
                // Un code a déjà été envoyé (login inclus) : démarrer le cooldown du renvoi.
                setResendCooldown(RESEND_COOLDOWN);
                toast.info(response.data.message || 'Un code de connexion vous a été envoyé par email.');
            }
        } catch (error) {
            // 401 (identifiants erronés), 403 (compte verrouillé), 429 (trop de demandes), 500 (envoi mail).
            const { message } = extractError(error);
            toast.error('Erreur : ' + message);
        } finally {
            setIsLoading(false);
        }
    };

    // Étape 2 : vérification du code 2FA.
    const handleVerify = async (event) => {
        event.preventDefault();

        setIsLoading(true);
        try {
            const response = await PrivateAxios.post('/login/verify-2fa', { code: code.trim().toUpperCase() });

            if (response.status === 200) {
                const token = response.data.token;
                const changePwd = response.data.change_pwd;

                if (changePwd) {
                    // Mot de passe à renouveler (> 180 jours ou jamais défini).
                    navigate('/change-password', { state: { token } });
                } else {
                    localStorage.setItem('assistance-axa-token', token);
                    navigate('/');
                }
            }
        } catch (error) {
            const { status, message } = extractError(error);
            toast.error(message);
            // Plus de connexion en attente → retour à l'écran identifiants.
            if (status === 401 && /Session 2FA/i.test(message)) {
                backToCredentials();
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Renvoi d'un nouveau code (invalide l'ancien).
    const handleResend = async () => {
        if (resendCooldown > 0 || isLoading) return;

        setIsLoading(true);
        try {
            const response = await PrivateAxios.post('/login/resend-2fa');

            if (response.status === 200) {
                toast.success(response.data.message || 'Un nouveau code vous a été envoyé par email.');
                setResendCooldown(RESEND_COOLDOWN);
            }
        } catch (error) {
            const { status, message, retryAfter } = extractError(error);
            toast.error(message);

            if (status === 429) {
                if (!Number.isNaN(retryAfter)) {
                    // Cooldown 60 s ou plafond (5 codes / 15 min) : caler le minuteur sur Retry-After.
                    setResendCooldown(retryAfter);
                } else {
                    // Garde-fou (3 renvois max / session) : pas de Retry-After → recommencer la connexion.
                    backToCredentials();
                }
            } else if (status === 401) {
                // Session 2FA expirée → retour à l'écran identifiants.
                backToCredentials();
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex h-screen">
            <ToastContainer position="top-right" autoClose={5000} closeOnClick className="custom-toast" />
            {!isOnline && (
                <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-gray-800 bg-opacity-50">
                    <div className="p-8 text-center rounded-xl bg-gray-50">
                        <h2 className="mb-4 text-2xl font-bold" style={{ color: '#00008F' }}>Application hors ligne</h2>
                        <p className="italic">Veuillez réessayer plus tard.</p>
                        <div className="flex items-center justify-center">
                            <img src="/img/maintenance.gif" className="w-60" alt="maintenance_img" />
                        </div>
                    </div>
                </div>
            )}
            <div className="hidden w-2/5 h-full lg:block">
                <img src="/img/pattern.png" className="object-cover w-full h-full rounded-r-lg" alt="pattern_img" />
            </div>
            <div className="flex items-center justify-center w-full p-8 md:w-3/5">
                <div className="w-full max-w-md">
                    <img className="w-40 mx-auto mb-12" src="/img/axa_logo_solid_rgb.png" alt="AXA Logo" />

                    {step === 'credentials' ? (
                        <>
                            <h1 className="mb-8 text-4xl font-bold text-center">Connexion</h1>
                            <form className="space-y-8" onSubmit={handleLogin}>
                                <div>
                                    <label htmlFor="username" className="block mb-1 text-lg font-medium text-gray-700">Login :</label>
                                    <input
                                        type="text"
                                        id="username"
                                        name="username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className="w-full p-2 border border-gray-400 rounded-md focus:outline-none focus:border-blue-400 focus:bg-gray-100"
                                        placeholder="username"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="password" className="block mb-1 text-lg font-medium text-gray-700">Mot de passe :</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full p-2 mb-6 border border-gray-400 rounded-md focus:outline-none focus:border-blue-400 focus:bg-gray-100"
                                        placeholder="**********"
                                        required
                                        autoComplete="false"
                                    />
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full p-2 mb-4 font-semibold text-white rounded-md bg-axa-blue-400 hover:bg-hover-axa-blue hover:shadow-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Connexion...' : 'Connexion'}
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <>
                            <h1 className="mb-2 text-4xl font-bold text-center">Vérification</h1>
                            <p className="mb-8 text-center text-gray-600">
                                Saisissez le code à 6 caractères reçu par email.
                            </p>
                            <form className="space-y-8" onSubmit={handleVerify}>
                                <div>
                                    <label htmlFor="code" className="block mb-1 text-lg font-medium text-gray-700">Code de connexion :</label>
                                    <input
                                        type="text"
                                        id="code"
                                        name="code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.toUpperCase())}
                                        maxLength={6}
                                        className="w-full p-2 text-2xl text-center uppercase border border-gray-400 rounded-md tracking-[0.5em] focus:outline-none focus:border-blue-400 focus:bg-gray-100"
                                        placeholder="------"
                                        autoComplete="one-time-code"
                                        autoFocus
                                        required
                                    />
                                </div>
                                <div className="space-y-3">
                                    <button
                                        type="submit"
                                        disabled={isLoading || code.trim().length !== 6}
                                        className="w-full p-2 font-semibold text-white rounded-md bg-axa-blue-400 hover:bg-hover-axa-blue hover:shadow-lg disabled:bg-gray-500 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Vérification...' : 'Valider'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        disabled={isLoading || resendCooldown > 0}
                                        className="w-full p-2 font-semibold border rounded-md text-axa-blue-400 border-axa-blue-400 hover:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed"
                                    >
                                        {resendCooldown > 0 ? `Renvoyer le code (${resendCooldown}s)` : 'Renvoyer le code'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={backToCredentials}
                                        disabled={isLoading}
                                        className="w-full text-sm text-center text-gray-500 hover:text-gray-700 hover:underline"
                                    >
                                        ← Retour à la connexion
                                    </button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Login;
